import React, { useRef, useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

export function AtomCollisionAnimation() {
    const mountRef = useRef(null);
    const animationFrameId = useRef(null);
    const controlsRef = useRef(null);
    const windowHalfX = useRef(window.innerWidth / 2);
    const windowHalfY = useRef(window.innerHeight / 2);
    
    // Add refs for mouse position and target rotation
    const mouseX = useRef(0);
    const mouseY = useRef(0);
    const targetRotationX = useRef(0);
    const targetRotationY = useRef(0);
    
    // Use useMemo for isMobile detection
    const isMobile = useMemo(() => {
        if (typeof window !== 'undefined') {
            return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
                window.innerWidth < 768 || 
                ('ontouchstart' in window);
        }
        return false;
    }, []);
    
    // Flag to completely disable controls on mobile
    const shouldEnableControls = !isMobile;

    useEffect(() => {
        if (!mountRef.current) return;

        const currentMount = mountRef.current;

        let group, camera, scene, renderer;
        let particles, linesMesh;
        let controls, clock;

        // SIMPLIFIED STARBURST PARAMETERS
        const lineCount = 340;
        const dotsPerLine = 1;
        const maxLineLength = 310;
        const minLineLength = 15;

        // COLORS
        const mainColor = new THREE.Color("#01CBA0");
        const secondColor = new THREE.Color("#01775D");

        function onWindowResize() {
            if (!currentMount || !renderer || !camera) return;
            const width = currentMount.clientWidth;
            const height = currentMount.clientHeight;

            if (width === 0 || height === 0) return;

            windowHalfX.current = width / 2;
            windowHalfY.current = height / 2;

            camera.aspect = width / height;
            camera.updateProjectionMatrix();
            renderer.setSize(width, height);
        }

        // Add mouse move handler
        function onDocumentMouseMove(event) {
            if (isMobile) return; // Skip on mobile
            
            mouseX.current = (event.clientX - windowHalfX.current);
            mouseY.current = (event.clientY - windowHalfY.current);
            
            // Calculate target rotation based on mouse position
            targetRotationX.current = mouseY.current * 0.002;
            targetRotationY.current = mouseX.current * 0.002;
        }

        function init() {
            const width = currentMount.clientWidth;
            const height = currentMount.clientHeight;

            if (width === 0 || height === 0) return;

            windowHalfX.current = width / 2;
            windowHalfY.current = height / 2;

            // Initialize clock
            clock = new THREE.Clock();

            // SCENE SETUP
            scene = new THREE.Scene();
            scene.background = null; // Transparent background

            // CAMERA
            camera = new THREE.PerspectiveCamera(75, width / height, 1, 1000);
            camera.position.z = 600;

            // GROUP
            group = new THREE.Group();
            scene.add(group);

            // Create data structures
            const particlePositions = [];
            const particleColors = [];
            const particleSizes = [];
            const particleData = [];
            const lineData = [];
            const linesPositions = [];
            const linesColors = [];

            // Create lines and dots
            for (let i = 0; i < lineCount; i++) {
                // Random direction
                const direction = new THREE.Vector3(
                    Math.random() * 2 - 1,
                    Math.random() * 2 - 1,
                    Math.random() * 2 - 1
                ).normalize();

                // Line length
                const lineLength = minLineLength + Math.random() * (maxLineLength - minLineLength);

                // Start and end points
                const startPoint = new THREE.Vector3(0, 0, 0);
                const endPoint = direction.clone().multiplyScalar(lineLength);

                // Store line data for animation
                lineData.push({
                    direction: direction,
                    length: lineLength,
                    startIndex: i * 6,
                    animOffset: Math.random() * Math.PI * 2,
                    speed: 0.2 + Math.random() * 5.1,
                    color: Math.random() > .01 ? mainColor.clone() : secondColor.clone()
                });

                // Add line positions
                linesPositions.push(
                    startPoint.x, startPoint.y, startPoint.z,
                    endPoint.x, endPoint.y, endPoint.z
                );

                // Line color
                const lineColor = lineData[i].color;
                linesColors.push(
                    lineColor.r, lineColor.g, lineColor.b, 1.0,
                    lineColor.r, lineColor.g, lineColor.b, 1.0
                );

                // Add dots along the line
                for (let j = 0; j < dotsPerLine; j++) {
                    // Calculate dot position
                    const t = j / (dotsPerLine - 0);
                    const dotPos = startPoint.clone().lerp(endPoint, t);

                    // Add dot position
                    particlePositions.push(dotPos.x, dotPos.y, dotPos.z);

                    // Dot color
                    particleColors.push(lineColor.r, lineColor.g, lineColor.b, 1.0);

                    // Dot size
                    const size = 1 + Math.random() * 1.5;
                    particleSizes.push(size);

                    // Store particle data
                    particleData.push({
                        lineIndex: i,
                        positionOnLine: Math.random(),
                        size: size,
                        animOffset: Math.random() * Math.PI * 2,
                        particleIndex: (i * dotsPerLine + j) * 4,
                        speed: 0.015 + Math.random() * 0.03
                    });
                }
            }

            // LINES GEOMETRY
            const linesGeometry = new THREE.BufferGeometry();
            linesGeometry.setAttribute('position', new THREE.Float32BufferAttribute(linesPositions, 3));
            linesGeometry.setAttribute('color', new THREE.Float32BufferAttribute(linesColors, 4));

            // LINES MATERIAL - with transparency
            const linesMaterial = new THREE.LineBasicMaterial({
                vertexColors: true,
                transparent: true,
                opacity: 0.8,
                depthWrite: false
            });

            // CREATE LINES
            linesMesh = new THREE.LineSegments(linesGeometry, linesMaterial);
            group.add(linesMesh);

            // PARTICLES GEOMETRY
            const particlesGeometry = new THREE.BufferGeometry();
            particlesGeometry.setAttribute('position', new THREE.Float32BufferAttribute(particlePositions, 3));
            particlesGeometry.setAttribute('color', new THREE.Float32BufferAttribute(particleColors, 4));
            particlesGeometry.setAttribute('size', new THREE.Float32BufferAttribute(particleSizes, 1));

            // Load texture for particles (simple dot)
            const textureLoader = new THREE.TextureLoader();
            const particleTexture = textureLoader.load('data:image/png;base64,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');

            // PARTICLES MATERIAL - with transparency
            const particlesMaterial = new THREE.PointsMaterial({
                size: 20, 
                vertexColors: true,
                map: particleTexture,
                transparent: true,
                sizeAttenuation: true,
                depthWrite: false
            });

            // CREATE PARTICLES
            particles = new THREE.Points(particlesGeometry, particlesMaterial);
            group.add(particles);

            // ADD CENTRAL LIGHT
            const pointLight = new THREE.PointLight(0x00FF88, 5, 1000);
            pointLight.position.set(0, 0, 0);
            scene.add(pointLight);

            // RENDERER with alpha
            renderer = new THREE.WebGLRenderer({
                antialias: true,
                alpha: true,
                premultipliedAlpha: false
            });
            renderer.setPixelRatio(window.devicePixelRatio);
            renderer.setSize(width, height);
            renderer.setClearColor(0x000000, 0); // Transparent
            
            // Clear the mount first
            while (currentMount.firstChild) {
                currentMount.removeChild(currentMount.firstChild);
            }
            
            // Style the canvas for transparency
            renderer.domElement.style.background = 'transparent';
            renderer.domElement.style.backgroundColor = 'transparent';
            renderer.domElement.style.position = 'absolute';
            renderer.domElement.style.top = '0';
            renderer.domElement.style.left = '0';
            
            // Append to DOM
            currentMount.appendChild(renderer.domElement);

            // CONTROLS - Only for desktop
            if (shouldEnableControls) {
                controls = new OrbitControls(camera, renderer.domElement);
                controls.enableDamping = true;
                controls.dampingFactor = 0.05;
                controls.enableZoom = false;
                controls.enablePan = false;
                controlsRef.current = controls;
                
                currentMount.style.cursor = 'grab';
                
                controls.addEventListener('start', () => {
                    currentMount.style.cursor = 'grabbing';
                });
                
                controls.addEventListener('end', () => {
                    currentMount.style.cursor = 'grab';
                });
            } else {
                controlsRef.current = null;
                
                if (renderer.domElement) {
                    renderer.domElement.style.touchAction = 'manipulation';
                    renderer.domElement.style.pointerEvents = 'auto';
                    renderer.domElement.addEventListener('touchstart', () => {}, { passive: true });
                }
            }

            // Store animation data
            animate.particleData = particleData;
            animate.lineData = lineData;
            animate.mainColor = mainColor;
            animate.secondColor = secondColor;
            animate.pointLight = pointLight;
            animate.clock = clock;
            animate.scene = scene;
            animate.camera = camera;

            // Add event listeners
            window.addEventListener('resize', onWindowResize);
            if (!isMobile) {
                document.addEventListener('mousemove', onDocumentMouseMove);
            }
        }

        function animate() {
            animationFrameId.current = requestAnimationFrame(animate);

            if (!renderer || !animate.scene || !animate.camera) {
                return;
            }

            const elapsedTime = animate.clock.getElapsedTime();

            // DYNAMIC ROTATION
            if (group) {
                if (!isMobile) {
                    // Mouse-based rotation with smooth interpolation
                    group.rotation.y += (targetRotationY.current - group.rotation.y) * 0.1;
                    group.rotation.x += (targetRotationX.current - group.rotation.x) * 0.1;
                } else {
                    // Automatic rotation for mobile
                    group.rotation.y += 0.003;
                    group.rotation.x += 0.002;
                }
            }

            // ANIMATE LINES
            if (linesMesh && linesMesh.geometry && animate.lineData) {
                const linePositions = linesMesh.geometry.attributes.position.array;
                const lineColors = linesMesh.geometry.attributes.color.array;

                // Update lines
                for (let i = 0; i < animate.lineData.length; i++) {
                    const line = animate.lineData[i];
                    const startColorIndex = line.startIndex / 6 * 8;
                    const endPosIndex = line.startIndex + 3;
                    const endColorIndex = startColorIndex + 4;

                    // Calculate new end position with subtle wave motion
                    const time = elapsedTime * line.speed + line.animOffset;
                    const pulseScale = 1 + 0.15 * Math.sin(time);

                    // New end position 
                    const newEnd = line.direction.clone().multiplyScalar(line.length * pulseScale);

                    // Update line end point position
                    linePositions[endPosIndex] = newEnd.x;
                    linePositions[endPosIndex + 1] = newEnd.y;
                    linePositions[endPosIndex + 2] = newEnd.z;

                    // Color transition
                    const colorBlend = (Math.sin(time * 0.5) + 1) * 0.5;
                    const blendedColor = animate.mainColor.clone().lerp(animate.secondColor, colorBlend);

                    // Update colors
                    lineColors[startColorIndex] = blendedColor.r;
                    lineColors[startColorIndex + 1] = blendedColor.g;
                    lineColors[startColorIndex + 2] = blendedColor.b;

                    lineColors[endColorIndex] = blendedColor.r;
                    lineColors[endColorIndex + 1] = blendedColor.g;
                    lineColors[endColorIndex + 2] = blendedColor.b;
                }

                linesMesh.geometry.attributes.position.needsUpdate = true;
                linesMesh.geometry.attributes.color.needsUpdate = true;
            }

            // ANIMATE PARTICLES
            if (particles && particles.geometry && animate.particleData) {
                const particlePositions = particles.geometry.attributes.position.array;
                const particleColors = particles.geometry.attributes.color.array;
                const particleSizes = particles.geometry.attributes.size.array;

                // Update particles
                for (let i = 0; i < animate.particleData.length; i++) {
                    const particle = animate.particleData[i];
                    const line = animate.lineData[particle.lineIndex];

                    // Get current line end position
                    const endPosIndex = line.startIndex + 3;
                    const endPos = new THREE.Vector3(
                        linesMesh.geometry.attributes.position.array[endPosIndex],
                        linesMesh.geometry.attributes.position.array[endPosIndex + 1],
                        linesMesh.geometry.attributes.position.array[endPosIndex + 2]
                    );

                    // Update particle position
                    particle.positionOnLine += particle.speed;
                    if (particle.positionOnLine > 1.0) {
                        particle.positionOnLine = 0.0;
                    }
                    let pos = particle.positionOnLine;

                    // Calculate new position 
                    const newPos = new THREE.Vector3().lerpVectors(
                        new THREE.Vector3(0, 0, 0),
                        endPos,
                        pos
                    );

                    // Update position
                    const positionIndex = i * 3;
                    particlePositions[positionIndex] = newPos.x;
                    particlePositions[positionIndex + 1] = newPos.y;
                    particlePositions[positionIndex + 2] = newPos.z;

                    // Get line color
                    const startColorIndex = line.startIndex / 6 * 8;
                    const colorR = linesMesh.geometry.attributes.color.array[startColorIndex];
                    const colorG = linesMesh.geometry.attributes.color.array[startColorIndex + 1];
                    const colorB = linesMesh.geometry.attributes.color.array[startColorIndex + 2];

                    // Update color
                    const colorIndex = particle.particleIndex;
                    particleColors[colorIndex] = colorR;
                    particleColors[colorIndex + 1] = colorG;
                    particleColors[colorIndex + 2] = colorB;

                    // Fade alpha based on position
                    const alphaValue = Math.max(0.05, 1.0 - pos);
                    particleColors[colorIndex + 3] = alphaValue;

                    // Pulse size
                    const time = elapsedTime * 0.2 + particle.animOffset;
                    const sizePulse = 1.0 + 0.5 * Math.sin(time * 1.5);
                    const sizeFactor = Math.max(0.1, (1 - pos * 0.7));
                    particleSizes[i] = particle.size * sizePulse * sizeFactor;
                }

                particles.geometry.attributes.position.needsUpdate = true;
                particles.geometry.attributes.color.needsUpdate = true;
                particles.geometry.attributes.size.needsUpdate = true;
            }

            // ANIMATE LIGHT
            if (animate.pointLight) {
                const lightIntensity = 4 + 2.5 * Math.sin(elapsedTime * 1.0);
                animate.pointLight.intensity = lightIntensity;

                const colorPulse = (Math.sin(elapsedTime * 0.6) + 1) * 0.5;
                const lightColor = animate.mainColor.clone().lerp(animate.secondColor, colorPulse);
                animate.pointLight.color.set(lightColor);
            }

            // Update controls
            if (shouldEnableControls && controlsRef.current) {
                controlsRef.current.update();
            }

            // Simple direct rendering with transparency
            renderer.setClearColor(0x000000, 0);
            renderer.clear();
            renderer.render(animate.scene, animate.camera);
        }

        // Initialize and animate
        init();
        animate();

        // Cleanup
        return () => {
            window.removeEventListener('resize', onWindowResize);            
            if (!isMobile) {
                document.removeEventListener('mousemove', onDocumentMouseMove);
            }
            if (controlsRef.current) {
                controlsRef.current.removeEventListener('start');
                controlsRef.current.removeEventListener('end');
                controlsRef.current.dispose();
                controlsRef.current = null;
            }

            if (animationFrameId.current) {
                cancelAnimationFrame(animationFrameId.current);
            }

            if (renderer) {
                if (currentMount && renderer.domElement && currentMount.contains(renderer.domElement)) {
                    currentMount.style.cursor = 'auto';
                    currentMount.removeChild(renderer.domElement);
                }
                renderer.dispose();
            }
            
            // Clear references
            camera = null;
            scene = null;
            renderer = null;
            group = null;
            particles = null;
            linesMesh = null;
            controls = null;
        };
    }, [shouldEnableControls]); 

    return <div ref={mountRef} style={{ 
        width: '100%', 
        height: '100%', 
        overflow: 'hidden',
        background: 'transparent',
        backgroundColor: 'transparent',
        backgroundImage: 'none',
        position: 'relative'
    }} />;
}